import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { calculators } from "@/data/calculators";
import { ChevronDown, ChevronRight, Calculator, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Sitemap() {
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [expandedSubcategories, setExpandedSubcategories] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [expandAll, setExpandAll] = useState(false);

  // Organize calculators by category and subcategory
  const organizedCalculators = calculators.reduce((acc, calc) => {
    const category = calc.category;
    const subcategory = calc.subcategory || "Other";
    
    if (!acc[category]) {
      acc[category] = {};
    }
    if (!acc[category][subcategory]) {
      acc[category][subcategory] = [];
    }
    acc[category][subcategory].push(calc);
    return acc;
  }, {} as Record<string, Record<string, typeof calculators>>);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleSubcategory = (key: string) => {
    setExpandedSubcategories(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleExpandAll = () => {
    const newExpandAll = !expandAll;
    setExpandAll(newExpandAll);
    
    if (newExpandAll) {
      const allCategories: Record<string, boolean> = {};
      const allSubcategories: Record<string, boolean> = {};
      
      Object.keys(organizedCalculators).forEach(category => {
        allCategories[category] = true;
        Object.keys(organizedCalculators[category]).forEach(subcategory => {
          allSubcategories[`${category}-${subcategory}`] = true;
        });
      });
      
      setExpandedCategories(allCategories);
      setExpandedSubcategories(allSubcategories);
    } else {
      setExpandedCategories({});
      setExpandedSubcategories({});
    }
  };

  // Filter calculators based on search
  const filteredCalculators = searchQuery
    ? calculators.filter(calc =>
        calc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        calc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        calc.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : null;

  const categoryLabels: Record<string, string> = {
    physics: "Physics",
    chemistry: "Chemistry",
    math: "Math",
    finance: "Finance",
    general: "General",
    tools: "Tools"
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      physics: "text-blue-500 border-blue-500/20 bg-blue-500/10",
      chemistry: "text-green-500 border-green-500/20 bg-green-500/10",
      math: "text-purple-500 border-purple-500/20 bg-purple-500/10",
      finance: "text-orange-500 border-orange-500/20 bg-orange-500/10",
      general: "text-pink-500 border-pink-500/20 bg-pink-500/10",
      tools: "text-cyan-500 border-cyan-500/20 bg-cyan-500/10"
    };
    return colors[category] || "text-foreground";
  };

  const getTotalCount = (category?: string, subcategory?: string) => {
    if (category && subcategory) {
      return organizedCalculators[category]?.[subcategory]?.length || 0;
    }
    if (category) {
      return Object.values(organizedCalculators[category] || {}).reduce(
        (sum, calcs) => sum + calcs.length, 
        0
      );
    }
    return calculators.length;
  };

  return (
    <PageLayout>
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold tracking-tight">Calculator Sitemap</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Browse all {calculators.length} calculators organized by category and specialty
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <CardTitle>Interactive Tree View</CardTitle>
                <CardDescription>
                  Click to expand categories and subcategories
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleExpandAll}
              >
                {expandAll ? "Collapse All" : "Expand All"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search calculators..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {searchQuery && filteredCalculators ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Found {filteredCalculators.length} calculator(s)
                </p>
                {filteredCalculators.map((calc) => (
                  <Link
                    key={calc.id}
                    to={calc.href}
                    className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors"
                  >
                    <calc.icon className="h-5 w-5 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{calc.title}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {calc.description}
                      </p>
                    </div>
                    <Badge variant="outline" className={getCategoryColor(calc.category)}>
                      {categoryLabels[calc.category]}
                    </Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {Object.entries(organizedCalculators).map(([category, subcategories]) => {
                  const isExpanded = expandedCategories[category];
                  
                  return (
                    <div key={category} className="border rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleCategory(category)}
                        className="w-full flex items-center gap-3 p-4 hover:bg-accent transition-colors text-left"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-5 w-5 flex-shrink-0" />
                        )}
                        <Calculator className={`h-5 w-5 flex-shrink-0 ${getCategoryColor(category)}`} />
                        <div className="flex-1">
                          <p className="font-semibold text-lg">
                            {categoryLabels[category] || category}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {getTotalCount(category)} calculators
                          </p>
                        </div>
                        <Badge variant="outline" className={getCategoryColor(category)}>
                          {Object.keys(subcategories).length} subcategories
                        </Badge>
                      </button>

                      {isExpanded && (
                        <div className="border-t bg-muted/30 p-2 space-y-1">
                          {Object.entries(subcategories).map(([subcategory, calcs]) => {
                            const subKey = `${category}-${subcategory}`;
                            const isSubExpanded = expandedSubcategories[subKey];

                            return (
                              <div key={subKey} className="border rounded-lg overflow-hidden bg-background">
                                <button
                                  onClick={() => toggleSubcategory(subKey)}
                                  className="w-full flex items-center gap-3 p-3 hover:bg-accent transition-colors text-left"
                                >
                                  {isSubExpanded ? (
                                    <ChevronDown className="h-4 w-4 flex-shrink-0 ml-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 flex-shrink-0 ml-4" />
                                  )}
                                  <div className="flex-1">
                                    <p className="font-medium">{subcategory}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {calcs.length} calculators
                                    </p>
                                  </div>
                                </button>

                                {isSubExpanded && (
                                  <div className="border-t p-2 space-y-1">
                                    {calcs.map((calc) => (
                                      <Link
                                        key={calc.id}
                                        to={calc.href}
                                        className="flex items-center gap-3 p-2 ml-8 rounded hover:bg-accent transition-colors group"
                                      >
                                        <calc.icon className="h-4 w-4 text-muted-foreground group-hover:text-primary flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium truncate group-hover:text-primary">
                                            {calc.title}
                                          </p>
                                          <p className="text-xs text-muted-foreground truncate">
                                            {calc.description}
                                          </p>
                                        </div>
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Statistics</CardTitle>
            <CardDescription>Calculator distribution across categories</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(organizedCalculators).map(([category, subcategories]) => (
                <div
                  key={category}
                  className={`p-4 rounded-lg border ${getCategoryColor(category)}`}
                >
                  <p className="font-semibold mb-1">
                    {categoryLabels[category] || category}
                  </p>
                  <p className="text-2xl font-bold mb-1">
                    {getTotalCount(category)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {Object.keys(subcategories).length} subcategories
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
